/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react'
import { useParams } from 'react-router-dom'

import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet'

import {
  List,
  Avatar,
  Typography,
  message,
  Row,
  Col,
  Flex,
  Button,
  Tooltip,
  Popconfirm,
} from 'antd'
import {
  HomeOutlined,
  BankOutlined,
  PushpinOutlined,
  DeleteOutlined,
} from '@ant-design/icons'

import { LatLngExpression } from 'leaflet'
import AddressModal from '~/components/DataManipulation/Address/AddressModal'

import { useCompanies } from '~/hooks/Companies/useCompanies'
import { useLocale } from '~/hooks/locale/useLocale'

import { ShowError } from '~/utils/errors/apiErrors'

import api from '~/services/api'
import { translate } from '~/utils/locale'

interface AddressProps {
  id: string
  notes: string
  type: string
  address: {
    zip_code: string
    street: string
    number: string
    complement: string
    neighborhood: string
    city: string
    id: string
    state: string
    location: { x: number; y: number }
  }
}

interface MapUpdateFunctionProps {
  centerView: LatLngExpression | undefined
  zoom: number
}

interface AddressItemProps {
  isVisualization?: boolean
  addressInfo: AddressProps
  refetch: () => void
}

const AddressItem: React.FC<AddressItemProps> = ({ addressInfo, refetch }) => {
  const [confirmLoading, setConfirmLoading] = useState(false)

  const { contactId } = useParams<{ contactId: string }>()

  const { selectedCompany } = useCompanies()
  const { locale } = useLocale()

  function ChangeView({ centerView, zoom }: MapUpdateFunctionProps) {
    const map = useMap()
    map.setView(centerView!, zoom)
    return null
  }

  const handleDelete = async (id: string) => {
    try {
      setConfirmLoading(true)
      await api.delete(
        `/company/${selectedCompany}/contact/${contactId}/address/${id}`,
      )
      setConfirmLoading(false)
      refetch()
      message.success(translate('AddressList.successMessage'))
    } catch (err: any) {
      ShowError(err.message, translate('AddressList.errorMessage'), locale)
      setConfirmLoading(false)
    }
  }

  const { Title, Text } = Typography

  return (
    <List.Item
      key={addressInfo.id}
      extra={
        <MapContainer
          center={
            addressInfo.address.location
              ? [addressInfo.address.location.x, addressInfo.address.location.y]
              : [0, 0]
          }
          zoom={17}
          scrollWheelZoom={false}
          style={{ height: '200px', width: '350px' }}
          zoomControl={false}
          dragging={false}
          boxZoom={false}
          doubleClickZoom={false}
        >
          <ChangeView
            centerView={
              addressInfo.address.location
                ? [
                    addressInfo.address.location.x,
                    addressInfo.address.location.y,
                  ]
                : [0, 0]
            }
            zoom={17}
          />
          <TileLayer
            attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {addressInfo.address.location && (
            <Marker
              position={[
                addressInfo.address.location.x,
                addressInfo.address.location.y,
              ]}
            />
          )}
        </MapContainer>
      }
    >
      <List.Item.Meta
        style={{ marginBlock: 0 }}
        title={
          <Flex align="center" gap={10}>
            <Title level={5} style={{ marginBottom: 0 }}>
              {addressInfo.type === 'personal'
                ? translate('AddressList.addressTypePersonal')
                : addressInfo.type === 'business'
                  ? translate('AddressList.addressTypeBusiness')
                  : translate('AddressList.addressTypeCharge')}
            </Title>

            <Flex align="center" gap={5}>
              <AddressModal
                isUpdate
                AddressInfo={addressInfo}
                refetch={refetch}
              />
              <Tooltip title={translate('AddressList.popconfirmTooltip')}>
                <Popconfirm
                  title={translate('AddressList.popconfirmText')}
                  onConfirm={() => handleDelete(addressInfo.address.id)}
                >
                  <Button
                    type="primary"
                    danger
                    icon={<DeleteOutlined />}
                    loading={confirmLoading}
                    size="small"
                  />
                </Popconfirm>
              </Tooltip>
            </Flex>
          </Flex>
        }
      />

      <Row style={{ margin: '14px 0' }}>
        <Col span={8}>
          <Flex gap={10}>
            <Avatar
              shape="square"
              icon={
                <PushpinOutlined style={{ fontSize: 12, color: '#464545' }} />
              }
              size="small"
              style={{ background: '#DADADA' }}
            />
            <div>
              <Title level={5} style={{ marginBottom: 0 }}>
                {translate('TasksTabsDetails.zipcodeText')}
              </Title>
              <Text>{addressInfo.address.zip_code}</Text>
            </div>
          </Flex>
        </Col>
        <Col span={12}>
          <Flex gap={10}>
            <Avatar
              shape="square"
              icon={<HomeOutlined style={{ fontSize: 12, color: '#464545' }} />}
              size="small"
              style={{ background: '#DADADA' }}
            />
            <div>
              <Title level={5} style={{ marginBottom: 0 }}>
                {translate('TasksTabsDetails.addressText')}
              </Title>
              <Text>
                {addressInfo.address.street}, {addressInfo.address.number} -{' '}
                {addressInfo.address.neighborhood}
              </Text>
            </div>
          </Flex>
        </Col>
      </Row>

      <Row>
        <Col span={8}>
          <Flex gap={8}>
            <Avatar
              shape="square"
              icon={<BankOutlined style={{ fontSize: 12, color: '#464545' }} />}
              size="small"
              style={{ background: '#DADADA' }}
            />
            <div>
              <Title level={5} style={{ marginBottom: 0 }}>
                {addressInfo.address.city}
              </Title>
              <Text>
                {addressInfo.address.city} - {addressInfo.address.state}
              </Text>
            </div>
          </Flex>
        </Col>

        <Col span={8}>
          <Flex gap={8}>
            <Avatar
              shape="square"
              icon={<BankOutlined style={{ fontSize: 12, color: '#464545' }} />}
              size="small"
              style={{ background: '#DADADA' }}
            />
            <div>
              <Title level={5} style={{ marginBottom: 0 }}>
                {translate('AddressList.complementText')}
              </Title>
              {addressInfo.address.complement ? (
                <Text>{addressInfo.address.complement}</Text>
              ) : (
                'Nenhuma informação disponível.'
              )}
            </div>
          </Flex>
        </Col>
      </Row>

      <Row style={{ marginTop: 14 }}>
        <Col span={8}>
          <Flex gap={8}>
            <Avatar
              shape="square"
              icon={<BankOutlined style={{ fontSize: 12, color: '#464545' }} />}
              size="small"
              style={{ background: '#DADADA' }}
            />
            <div>
              <Title level={5} style={{ marginBottom: 0 }}>
                {translate('AddressList.typeText')}
              </Title>
              {addressInfo.type === 'personal'
                ? translate('AddressList.typePersonalText')
                : addressInfo.type === 'business'
                  ? translate('AddressList.typeBusinessText')
                  : translate('AddressList.typeChargeText')}
            </div>
          </Flex>
        </Col>

        <Col span={8}>
          <Flex gap={8}>
            <Avatar
              shape="square"
              icon={<BankOutlined style={{ fontSize: 12, color: '#464545' }} />}
              size="small"
              style={{ background: '#DADADA' }}
            />
            <div>
              <Title level={5} style={{ marginBottom: 0 }}>
                {translate('AddressList.observationText')}
              </Title>
              {addressInfo.notes ? (
                <Text>{addressInfo.notes}</Text>
              ) : (
                'Nenhuma informação disponível.'
              )}
            </div>
          </Flex>
        </Col>
      </Row>
    </List.Item>
  )
}

export default AddressItem
